import React from 'react';
import Pusher from 'pusher-js';

// TODO: Discuss context setup with types
// TODO: Discuss a hook model with context

type PusherState = 'initialized' | 'connecting' | 'connected' | 'disconnected' | 'unavailable' | 'failed';

export const SocketContext = React.createContext<{
	pusherInstance: Pusher | null;
	bindChannelEvent: (channelName: string, eventName: string, callback: (data: any) => void) => (() => void) | void;
	bindAuthChannelEvent: (channelName: string, eventName: string, callback: (data: any) => void) => (() => void) | void;
}>({
	pusherInstance: null,
	bindChannelEvent: () => undefined,
	bindAuthChannelEvent: () => undefined,
});

type SocketProviderProps = {
	isSignedIn?: boolean;
	name?: string;
	pusherKey: string;
	pusherCluster: string;
	pusherHost: string;
	pusherAuthEndpoint?: string;
	token?: string | null;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({
	children,
	isSignedIn,
	name,
	pusherKey,
	pusherCluster,
	pusherHost,
	pusherAuthEndpoint,
	token,
}) => {
	const pusherRef = React.useRef<Pusher | null>(null);
	const [autParamsSet, setAuthParamsSet] = React.useState(false);
	const [pusherState, setPusherState] = React.useState<PusherState>('initialized');
	const pusherConnected = pusherState === 'connected';

	React.useEffect(() => {
		Pusher.logToConsole = false;
		pusherRef.current = new Pusher(pusherKey, {
			cluster: pusherCluster,
			wsHost: pusherHost,
			wsPort: 6001,
			auth: {
				headers: {},
			},
			enabledTransports: ['ws', 'wss'],
			authEndpoint: pusherAuthEndpoint,
		});

		pusherRef.current.connection.bind(
			'state_change',
			(stateUpdate: { current: PusherState; previous: PusherState }) => {
				if (window.portal?.bugsnag?.leaveBreadcrumb) {
					window.portal?.bugsnag.leaveBreadcrumb(`${name} Websocket Status changed`, stateUpdate, 'state');
				}
				setPusherState(stateUpdate.current);
			}
		);

		// disabled as it was just causing a lot of noise in bugsnag
		// pusherRef.current.connection.bind('error', (error: Error) => {
		// 	if (window.portal?.bugsnag?.notify) window.portal.bugsnag.notify(error);
		// 	//   console.log("pusher error", error);
		// });

		return () => {
			pusherRef.current?.unbind();
			pusherRef.current?.disconnect();
			pusherRef.current = null;
		};
	}, [name, pusherKey, pusherCluster, pusherHost, pusherAuthEndpoint]);

	// this effect will update the headers for socket authentication
	React.useEffect(() => {
		if (pusherRef.current?.config.auth?.headers)
			pusherRef.current.config.auth.headers = {
				Accept: 'application/json',
				Authorization: `Bearer ${token ?? ''}`,
			};

		setAuthParamsSet(!!isSignedIn && !!token);
	}, [token, isSignedIn]);

	const bindChannelEvent = React.useCallback(
		(channelName: string, eventName: string, callback: (data: any) => void) => {
			if (!pusherConnected || !pusherRef.current) {
				return;
			}

			const channel = pusherRef.current.subscribe(channelName);
			channel?.bind(eventName, callback);

			// eslint-disable-next-line consistent-return
			return () => {
				channel?.unbind(eventName, callback);
				if (!channel.callbacks.get(eventName)) {
					channel.unsubscribe();
				}
			};
		},
		[pusherConnected]
	);

	// all channels whose name starts with 'private-' need to be binded using this function
	const bindAuthChannelEvent = React.useCallback(
		(channelName: string, eventName: string, callback: (data: any) => void) => {
			if (!autParamsSet) {
				return;
			}

			return bindChannelEvent(channelName, eventName, callback);
		},
		[bindChannelEvent, autParamsSet]
	);

	return (
		<SocketContext.Provider
			value={{
				pusherInstance: pusherRef.current,
				bindAuthChannelEvent,
				bindChannelEvent,
			}}
			// I don't have a huge issue with this, but understand why this rule is here. Discuss? - Carter
			// eslint-disable-next-line react/no-children-prop
			children={children}
		/>
	);
};

SocketProvider.defaultProps = {
	isSignedIn: false,
	pusherAuthEndpoint: undefined,
	token: undefined,
};
